import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { Slider } from '../../components/ui/form';
import { ApiError } from '../../lib/api';
import { subscribeToUser } from '../../lib/realtime';
import { useDocumentTitle } from '../../lib/title';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { AbPlayer, type Side } from './AbPlayer';
import { INTENSITY_STOPS, mastering, type Preset, type Render, type UserEq, type WorkbenchTrack } from './api';
import { PresetGlyph } from './PresetGlyph';

/**
 * The mastering desk.
 *
 * Your tracks on the left; the one you are working on in the middle with
 * the Original / Mastered toggle over its waveform; the eight tiles, the
 * intensity slider and the three knobs under it. Nothing renders until
 * "Master it" is pressed: a render is a paid call to the engine and a
 * moved knob is not a decision yet. Once it is pressed, the page waits on
 * the socket (`mastering.changed`) with a poll behind it, and the Mastered
 * side lights up when the file is back.
 *
 * Reference matching sits above the player and is off until a reference
 * has been analysed; then it is a switch that adds the matched curve to
 * whichever tile is chosen.
 *
 * ## A tool, not a page
 *
 * It fills the space between the header and the bottom of the window and
 * the document does not scroll: the rail scrolls, the desk scrolls, each
 * on its own. The site's dock is gone while you are here (`AppShell`), and
 * the site player is paused on the way in so its song and the desk's are
 * never heard together.
 */

/** The viewport minus the header; the shell adds no padding on a full-height screen. */
const FULL = 'h-[calc(100dvh-var(--spacing-chrome-top))]';
export function MasteringPage() {
  useDocumentTitle('Mastering');
  const client = useQueryClient();
  const me = useSession((s) => s.user);

  const workbench = useQuery({ queryKey: ['mastering', 'workbench'], queryFn: () => mastering.workbench() });

  // The site player, silenced on entry. Its dock is hidden here, so this is
  // the only way the person could have stopped it.
  useEffect(() => {
    const player = usePlayer.getState();

    if (player.track && player.playing) player.toggle();
  }, []);
  const [trackId, setTrackId] = useState<number | null>(null);
  const [preset, setPreset] = useState<string>('universal');
  const [intensity, setIntensity] = useState(1);
  const [eq, setEq] = useState<UserEq>({ low_db: 0, mid_db: 0, high_db: 0 });
  const [eqOn, setEqOn] = useState(true);
  const [useReference, setUseReference] = useState(false);
  const [side, setSide] = useState<Side>('preview');
  const [problem, setProblem] = useState<string | null>(null);

  const tracks = workbench.data?.tracks ?? [];
  const presets = workbench.data?.presets ?? [];
  const track = tracks.find((t) => t.id === trackId) ?? tracks.find((t) => t.ready) ?? null;

  useEffect(() => {
    if (track && trackId !== track.id) setTrackId(track.id);
  }, [track, trackId]);

  // The socket says a render or a reference moved; the query is the truth.
  useEffect(() => {
    if (!me) return;

    return subscribeToUser(me.id, (event) => {
      if (event === 'mastering.changed') {
        void client.invalidateQueries({ queryKey: ['mastering'] });
      }
    });
  }, [me, client]);

  // And a poll behind the socket while anything is rendering.
  const busy = tracks.some((t) => t.latest && (t.latest.status === 'queued' || t.latest.status === 'rendering'));

  useEffect(() => {
    if (!busy) return;

    const timer = setInterval(() => void client.invalidateQueries({ queryKey: ['mastering'] }), 8000);

    return () => clearInterval(timer);
  }, [busy, client]);

  const renders = useQuery({
    queryKey: ['mastering', 'renders', track?.id],
    queryFn: () => mastering.renders(track!.id),
    enabled: track !== null,
  });

  const original = useQuery({
    queryKey: ['mastering', 'original', track?.id],
    queryFn: () => mastering.original(track!.id),
    enabled: track !== null && track.ready,
    // The link lives fifteen minutes; asked again a little before that.
    staleTime: 12 * 60_000,
    refetchInterval: 13 * 60_000,
  });

  /*
   * The preview's chain: what a render would send, composed on the server
   * from the current tile, slider and knobs, a beat after the last change.
   * Nothing reaches the engine; the browser graph approximates it live.
   */
  const settings = {
    preset,
    intensity,
    user_eq: eqOn ? eq : { low_db: 0, mid_db: 0, high_db: 0 },
    use_reference: useReference && track?.reference?.state === 'ready',
  };
  const [debounced, setDebounced] = useState(settings);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(settings), 150);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, intensity, eq.low_db, eq.mid_db, eq.high_db, eqOn, useReference, track?.reference?.state]);

  const composed = useQuery({
    queryKey: ['mastering', 'compose', track?.id, debounced],
    queryFn: () => mastering.compose(track!.id, debounced),
    enabled: track !== null && track.ready,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });

  const latest = track?.latest ?? null;
  const shown = useMemo(() => {
    const done = (renders.data?.items ?? []).find((r) => r.status === 'done');

    return latest?.status === 'done' ? latest : (done ?? latest);
  }, [latest, renders.data]);

  // When the chosen track changes, adopt its newest render's settings.
  const adopted = useRef<number | null>(null);

  useEffect(() => {
    if (!track || adopted.current === track.id) return;

    adopted.current = track.id;

    if (track.latest) {
      setPreset(track.latest.preset);
      setIntensity(track.latest.intensity);
      setEq(track.latest.user_eq);
      setUseReference(track.latest.with_reference);
    }
  }, [track]);

  const render = useMutation({
    mutationFn: (id: number) =>
      mastering.render(id, {
        preset,
        intensity,
        user_eq: eqOn ? eq : { low_db: 0, mid_db: 0, high_db: 0 },
        use_reference: useReference && track?.reference?.state === 'ready',
      }),
    onSuccess: () => {
      setProblem(null);
      setSide('mastered');
      void client.invalidateQueries({ queryKey: ['mastering'] });
    },
    onError: (error) => setProblem(error instanceof ApiError ? error.message : 'That did not go through.'),
  });

  const renderAll = useMutation({
    mutationFn: async () => {
      for (const t of tracks.filter((t) => t.ready)) {
        await mastering.render(t.id, {
          preset,
          intensity,
          user_eq: eqOn ? eq : { low_db: 0, mid_db: 0, high_db: 0 },
          use_reference: false,
        });
      }
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mastering'] }),
    onError: (error) => setProblem(error instanceof ApiError ? error.message : 'That did not go through.'),
  });

  const keep = useMutation({
    mutationFn: (renderId: number) => mastering.keep(renderId),
    onSuccess: ({ url }) => {
      window.location.assign(url);
      void client.invalidateQueries({ queryKey: ['mastering'] });
    },
    onError: (error) => setProblem(error instanceof ApiError ? error.message : 'The master could not be fetched.'),
  });

  // Whether the desk's settings differ from the render being heard.
  const dirty =
    shown === null ||
    shown.preset !== preset ||
    Math.abs(shown.intensity - intensity) > 0.001 ||
    JSON.stringify(shown.user_eq) !== JSON.stringify(eqOn ? eq : { low_db: 0, mid_db: 0, high_db: 0 }) ||
    shown.with_reference !== (useReference && track?.reference?.state === 'ready');

  if (workbench.isPending) {
    return (
      <div className={FULL}>
        <LoadingPanel label="Opening the desk" className="mt-16" />
      </div>
    );
  }

  if (workbench.isError || !workbench.data) {
    return (
      <div className={`${FULL} px-6 py-10`}>
        <Alert tone="error">Could not open mastering.</Alert>
      </div>
    );
  }

  const rendering = latest !== null && (latest.status === 'queued' || latest.status === 'rendering');

  return (
    <div className={`${FULL} flex min-h-0 flex-col overflow-hidden lg:flex-row`} data-mastering-page>
      {/* ---- the rail: fixed width, scrolls on its own ------------------ */}
      <aside className="flex w-full shrink-0 flex-col border-b border-line lg:w-[320px] lg:border-b-0 lg:border-r">
        <div className="flex items-baseline justify-between px-5 pt-5">
          <h2 className="text-lg font-semibold text-ink">Your tracks</h2>
          <span className="text-xs text-faint">{tracks.length}</span>
        </div>
        <p className="px-5 pt-1 text-xs text-faint">Upload from the Drop a track page; anything ready lands here.</p>

        <ul className="mt-3 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 pb-3" data-mastering-tracks>
          {tracks.map((t, index) => (
            <TrackRow key={t.id} track={t} index={index} selected={t.id === track?.id} presets={presets} onSelect={() => setTrackId(t.id)} />
          ))}
          {tracks.length === 0 && <li className="py-6 text-center text-sm text-faint">Nothing uploaded yet.</li>}
        </ul>

        <div className="border-t border-line p-3">
          <Button
            variant="secondary"
            full
            disabled={tracks.filter((t) => t.ready).length < 2 || renderAll.isPending}
            loading={renderAll.isPending}
            onClick={() => renderAll.mutate()}
            data-master-all
          >
            Apply to all tracks
          </Button>
        </div>
      </aside>

      {/* ---- the desk: fills the rest, scrolls only if it must ---------- */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
        {!workbench.data.enabled && <Alert tone="error">The mastering engine is not switched on here.</Alert>}

        {track && (
          <ReferencePanel
            track={track}
            on={useReference}
            onToggle={setUseReference}
            onChange={() => void client.invalidateQueries({ queryKey: ['mastering'] })}
          />
        )}

        {track ? (
          <Card className="flex flex-1 flex-col justify-center gap-5 p-5" style={{ minHeight: 260 }}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h1 className="text-xl font-semibold tracking-tight text-ink">{track.title}</h1>
              <Readout render={shown} rendering={rendering} />
            </div>

            <AbPlayer
              originalUrl={original.data?.url ?? null}
              masteredUrl={shown?.status === 'done' ? shown.preview_url : null}
              peaks={shown?.waveform?.peaks ?? null}
              duration={shown?.waveform?.duration_sec ?? (track.duration_ms ?? 0) / 1000}
              side={side}
              onSide={setSide}
              preview={composed.data ?? null}
            />

            {shown?.already_mastered && (
              <p className="text-xs text-faint" data-already-mastered>
                This already sounds mastered, so the engine kept it light: no compression or saturation, half the EQ, level only.
              </p>
            )}
            {latest?.status === 'failed' && latest.error && (
              <Alert tone="error">{failureCopy(latest.error.code, latest.error.message)}</Alert>
            )}
          </Card>
        ) : (
          <Card className="flex flex-1 items-center justify-center p-8 text-center text-sm text-soft">
            Upload a track first; it appears here once it is ready.
          </Card>
        )}

        {/* Tiles */}
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-8" role="radiogroup" aria-label="Preset" data-presets>
          {presets.map((p) => (
            <Tile key={p.slug} preset={p} on={p.slug === preset} onClick={() => setPreset(p.slug)} />
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="flex flex-col gap-4 p-5">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-ink">Intensity</span>
              <span className="font-mono text-xs text-faint">{intensityLabel(intensity)}</span>
            </div>
            <Slider
              min={0}
              max={2}
              step={0.5}
              value={intensity}
              onChange={(event) => setIntensity(Number(event.target.value))}
              aria-label="Intensity"
              data-intensity
            />
            <div className="flex justify-between text-xs text-faint">
              {INTENSITY_STOPS.filter((s) => s.label).map((s) => (
                <span key={s.value}>{s.label}</span>
              ))}
            </div>
          </Card>

          <Card className="flex flex-col gap-4 p-5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-medium text-ink">
                EQ <Chip tone="signal">Beta</Chip>
              </span>
              <label className="flex items-center gap-2 text-xs text-faint">
                <input type="checkbox" checked={eqOn} onChange={(e) => setEqOn(e.target.checked)} data-eq-on />
                On
              </label>
            </div>
            <div className={`grid grid-cols-3 gap-4 ${eqOn ? '' : 'opacity-40'}`}>
              <Knob label="Low" value={eq.low_db} onChange={(v) => setEq({ ...eq, low_db: v })} disabled={!eqOn} />
              <Knob label="Mid" value={eq.mid_db} onChange={(v) => setEq({ ...eq, mid_db: v })} disabled={!eqOn} />
              <Knob label="High" value={eq.high_db} onChange={(v) => setEq({ ...eq, high_db: v })} disabled={!eqOn} />
            </div>
          </Card>
        </div>

        <div className="flex flex-wrap items-center gap-3 pb-1">
          <Button
            disabled={!track?.ready || rendering || !workbench.data.enabled}
            loading={render.isPending}
            onClick={() => track && render.mutate(track.id)}
            data-master
          >
            {rendering ? 'Rendering…' : dirty ? 'Master it' : 'Master it again'}
          </Button>
          {shown?.status === 'done' && shown.has_master && (
            <Button variant="secondary" loading={keep.isPending} onClick={() => keep.mutate(shown.id)} data-download-master>
              Download WAV
            </Button>
          )}
          <button
            type="button"
            className="text-sm text-faint hover:text-ink"
            onClick={() => {
              setIntensity(1);
              setEq({ low_db: 0, mid_db: 0, high_db: 0 });
              setEqOn(true);
            }}
          >
            Reset
          </button>
          {problem && <span className="text-sm text-hot-text">{problem}</span>}
          {!dirty && shown?.status === 'done' && <span className="text-xs text-faint">This is what you are hearing.</span>}
        </div>
      </div>
    </div>
  );
}

function TrackRow({
  track,
  index,
  selected,
  presets,
  onSelect,
}: {
  track: WorkbenchTrack;
  index: number;
  selected: boolean;
  presets: Preset[];
  onSelect: () => void;
}) {
  const latest = track.latest;
  const presetName = latest ? (presets.find((p) => p.slug === latest.preset)?.name ?? latest.preset) : null;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        disabled={!track.ready}
        data-mastering-track={track.id}
        aria-current={selected ? 'true' : undefined}
        className={`flex w-full items-center gap-3 rounded-[var(--radius-control)] px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
          selected ? 'bg-raised' : 'hover:bg-raised/60'
        }`}
      >
        <span className="w-4 shrink-0 font-mono text-[11px] text-ghost">{index + 1}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{track.title}</p>
          <p className="truncate text-xs text-faint">
            {!track.ready
              ? 'Processing'
              : latest === null
                ? 'Not mastered yet'
                : latest.status === 'done'
                  ? `${presetName} · ${intensityLabel(latest.intensity)}${latest.with_reference ? ' · matched' : ''}`
                  : latest.status === 'failed'
                    ? 'Failed'
                    : 'Rendering…'}
          </p>
        </div>
        {latest?.status === 'rendering' && <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-accent" />}
        {latest?.kept && <span className="text-[10px] text-faint">saved</span>}
      </button>
    </li>
  );
}

function Tile({ preset, on, onClick }: { preset: Preset; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onClick}
      title={`${preset.blurb} Target ${preset.target_lufs} LUFS.`}
      data-preset={preset.slug}
      className={`flex flex-col items-center gap-2 rounded-[var(--radius-card)] border px-2 py-4 transition-colors ${
        on ? 'border-accent bg-raised text-ink' : 'border-line text-soft hover:border-line-strong hover:text-ink'
      }`}
    >
      <PresetGlyph icon={preset.icon} className="h-7 w-7" />
      <span className="text-xs font-medium">{preset.name}</span>
    </button>
  );
}

/** A knob, drawn as a vertical slider with its value: ±6 dB in half steps. */
function Knob({ label, value, onChange, disabled }: { label: string; value: number; onChange: (v: number) => void; disabled: boolean }) {
  return (
    <label className="flex flex-col items-center gap-2 text-xs text-soft">
      <span className="font-mono text-[11px] text-faint">
        {value > 0 ? '+' : ''}
        {value.toFixed(1)} dB
      </span>
      <Slider
        min={-6}
        max={6}
        step={0.5}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-label={`${label} EQ`}
        data-eq={label.toLowerCase()}
      />
      <span>{label}</span>
    </label>
  );
}

function Readout({ render, rendering }: { render: Render | null; rendering: boolean }) {
  if (rendering) {
    return (
      <span className="flex items-center gap-2 text-xs text-faint" data-rendering>
        <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
        Rendering, usually under a minute
      </span>
    );
  }

  const out = render?.measured?.output;

  if (!out) return null;

  return (
    <span className="font-mono text-xs text-faint" data-readout title="What the master measures, not what was asked for">
      {out.integrated_lufs.toFixed(1)} LUFS · {out.true_peak_dbtp.toFixed(1)} dBTP · LRA {out.loudness_range_lu.toFixed(1)}
      {render?.measured?.render?.loudness_mode === 'dynamic' ? ' · squeezed to fit' : ''}
    </span>
  );
}

/**
 * Reference matching: a track of somebody else's, analysed for its tone
 * and then forgotten. The file goes straight from the browser to a
 * short-lived key and is deleted the moment the engine has read it.
 */
function ReferencePanel({
  track,
  on,
  onToggle,
  onChange,
}: {
  track: WorkbenchTrack;
  on: boolean;
  onToggle: (on: boolean) => void;
  onChange: () => void;
}) {
  const file = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (chosen: File) => {
      const type = chosen.type || 'audio/wav';
      const target = await mastering.referenceInit(track.id, type, chosen.size);
      const put = await fetch(target.url, { method: 'PUT', headers: target.headers, body: chosen });

      if (!put.ok) throw new Error(`Upload failed (${put.status})`);

      await mastering.referenceComplete(track.id, target.key, chosen.name.replace(/\.[a-z0-9]+$/i, ''));
    },
    onSuccess: () => {
      setProblem(null);
      onChange();
    },
    onError: (error) => setProblem(error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'That did not go through.'),
  });

  const clear = useMutation({
    mutationFn: () => mastering.referenceClear(track.id),
    onSuccess: () => {
      onToggle(false);
      onChange();
    },
  });

  const reference = track.reference;

  return (
    <Card className="flex flex-wrap items-center justify-between gap-3 p-4" data-reference-panel>
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-medium text-ink">
          Reference matching <Chip tone="signal">Beta</Chip>
        </p>
        <p className="text-xs text-faint">
          {reference === null
            ? 'Import any song and the master matches its tone. The file is analysed and deleted; only its tone is kept.'
            : reference.state === 'analysing'
              ? `Listening to ${reference.label}…`
              : reference.state === 'pending'
                ? `${reference.label} is ready; master once and it applies.`
                : `Matching ${reference.label}.`}
        </p>
        {problem && <p className="mt-1 text-xs text-hot-text">{problem}</p>}
      </div>

      <div className="flex items-center gap-2">
        {reference !== null && reference.state === 'ready' && (
          <label className="flex items-center gap-2 text-xs text-soft">
            <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} data-use-reference />
            Use it
          </label>
        )}
        {reference !== null && (
          <Button variant="ghost" loading={clear.isPending} onClick={() => clear.mutate()} data-clear-reference>
            Forget
          </Button>
        )}
        <input
          ref={file}
          type="file"
          accept="audio/*,.wav,.flac,.aiff,.mp3,.m4a"
          className="hidden"
          onChange={(event) => {
            const chosen = event.target.files?.[0];

            if (chosen) upload.mutate(chosen);

            event.target.value = '';
          }}
          data-reference-file
        />
        <Button variant="secondary" loading={upload.isPending} onClick={() => file.current?.click()} data-reference-pick>
          {reference === null ? 'Import a reference' : 'Replace'}
        </Button>
      </div>
    </Card>
  );
}

function intensityLabel(value: number): string {
  if (value <= 0.25) return 'Light';
  if (value < 0.75) return 'Light+';
  if (value <= 1.25) return 'Normal';
  if (value < 1.75) return 'Normal+';

  return 'Heavy';
}

function failureCopy(code: string, message: string): string {
  switch (code) {
    case 'too_short':
      return 'That track is under ten seconds; the engine needs more to work with.';
    case 'too_quiet':
      return 'That track is silent, or close to it.';
    case 'too_long':
      return 'That track is longer than the engine takes in one go.';
    case 'renders_per_day_exceeded':
    case 'mastering_busy':
      return 'The engine has done all it can today. Try again tomorrow.';
    default:
      return message || 'The render failed. Try again in a moment.';
  }
}
